/**
 * Nafath Verification Component
 *
 * UI for identity verification via Nafath app.
 * Currently shows "Coming Soon" state until API integration is ready.
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Card, { CardHeader, CardTitle, CardDescription, CardContent } from '../base/Card';
import Badge from '../base/Badge';

export type NafathStatus =
  | 'not_started'
  | 'pending'
  | 'waiting'
  | 'completed'
  | 'failed'
  | 'rejected'
  | 'expired';

/**
 * Maps the uppercase status the backend returns (WAITING | COMPLETED |
 * REJECTED | EXPIRED) into the lowercase enum the component renders.
 * Anything else falls back to 'pending'.
 */
export function mapApiStatusToNafathStatus(apiStatus: string | undefined): NafathStatus {
  switch (apiStatus) {
    case 'WAITING':   return 'waiting';
    case 'COMPLETED': return 'completed';
    case 'REJECTED':  return 'rejected';
    case 'EXPIRED':   return 'expired';
    default:          return 'pending';
  }
}

export interface NafathVerificationProps {
  status?: NafathStatus;
  verificationCode?: string;
  qrCodeUrl?: string;
  onInitiate?: () => void;
  onCancel?: () => void;
  onRetry?: () => void;
  isApiReady?: boolean; // When false, shows "Coming Soon"
  className?: string;
}

export default function NafathVerification({
  status = 'not_started',
  verificationCode,
  qrCodeUrl,
  onInitiate,
  onCancel,
  onRetry,
  isApiReady = false,
  className = '',
}: NafathVerificationProps) {
  const { t } = useTranslation('verification');
  const [showQR, setShowQR] = useState(false);

  // Coming Soon state when API is not ready
  if (!isApiReady) {
    return (
      <Card variant="bordered" className={className}>
        <CardContent>
          <div className="text-center py-8">
            {/* Nafath Logo/Icon */}
            <div className="w-20 h-20 mx-auto mb-6 rounded-2xl bg-green-600 flex items-center justify-center">
              <i className="ri-fingerprint-line text-4xl text-white"></i>
            </div>

            <h3 className="text-xl font-bold text-[var(--pv-color-text)] mb-2">
              {t('nafath.title')}
            </h3>
            <p className="text-[var(--pv-color-text-muted)] mb-6 max-w-sm mx-auto">
              {t('nafath.description')}
            </p>

            {/* Coming Soon Badge */}
            <Badge variant="warning" icon="ri-time-line" size="lg" className="mb-6">
              {t('comingSoon')}
            </Badge>

            <p className="text-sm text-[var(--pv-color-text-muted)]">
              {t('nafath.apiPending')}
            </p>

            {/* Disabled Button */}
            <button
              disabled
              className="mt-6 w-full max-w-xs px-6 py-3 bg-gray-200 dark:bg-gray-700 text-gray-500 dark:text-gray-400 rounded-xl font-medium cursor-not-allowed"
            >
              <i className="ri-fingerprint-line mr-2"></i>
              {t('nafath.initiate')}
            </button>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Not Started State
  if (status === 'not_started') {
    return (
      <Card variant="bordered" className={className}>
        <CardContent>
          <div className="text-center py-8">
            <div className="w-20 h-20 mx-auto mb-6 rounded-2xl bg-green-600 flex items-center justify-center">
              <i className="ri-fingerprint-line text-4xl text-white"></i>
            </div>

            <h3 className="text-xl font-bold text-[var(--pv-color-text)] mb-2">
              {t('nafath.title')}
            </h3>
            <p className="text-[var(--pv-color-text-muted)] mb-6">
              {t('nafath.description')}
            </p>

            <button
              onClick={onInitiate}
              className="w-full max-w-xs px-6 py-3 bg-green-600 text-white rounded-xl font-medium hover:shadow-lg transition-shadow"
            >
              <i className="ri-fingerprint-line mr-2"></i>
              {t('nafath.initiate')}
            </button>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Waiting State
  if (status === 'pending' || status === 'waiting') {
    return (
      <Card variant="bordered" className={className}>
        <CardHeader>
          <CardTitle>{t('nafath.waitingTitle')}</CardTitle>
          <CardDescription>{t('nafath.waitingDescription')}</CardDescription>
        </CardHeader>

        <CardContent>
          <div className="text-center py-6">
            {/* Verification Code */}
            {verificationCode && (
              <div className="mb-8">
                <p className="text-sm text-[var(--pv-color-text-muted)] mb-2">
                  {t('nafath.codeLabel')}
                </p>
                <div className="inline-flex items-center gap-3 px-8 py-4 bg-green-50 dark:from-green-900/30 dark:to-green-800/30 rounded-2xl border-2 border-green-200 dark:border-green-700">
                  <span className="text-4xl font-bold tracking-widest text-green-600 dark:text-green-400">
                    {verificationCode}
                  </span>
                </div>
              </div>
            )}

            {/* QR Code Toggle */}
            {qrCodeUrl && (
              <div className="mb-8">
                <button
                  onClick={() => setShowQR(!showQR)}
                  className="text-sm text-[var(--pv-color-primary)] hover:underline"
                >
                  {t('nafath.scanQR')}
                </button>

                {showQR && (
                  <div className="mt-4 inline-block p-4 bg-white rounded-xl">
                    <img src={qrCodeUrl} alt="QR Code" className="w-48 h-48" />
                  </div>
                )}
              </div>
            )}

            {/* Loading Animation */}
            <div className="flex items-center justify-center gap-2 text-[var(--pv-color-text-muted)]">
              <i className="ri-loader-4-line animate-spin"></i>
              <span>{t('nafath.status.pending')}</span>
            </div>

            {/* Cancel Button */}
            <button
              onClick={onCancel}
              className="mt-6 px-6 py-2 text-[var(--pv-color-text-muted)] hover:text-[var(--pv-color-text)] transition-colors"
            >
              {t('nafath.cancel')}
            </button>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Completed State
  if (status === 'completed') {
    return (
      <Card variant="bordered" className={className}>
        <CardContent>
          <div className="text-center py-8">
            <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
              <i className="ri-checkbox-circle-fill text-4xl text-green-500"></i>
            </div>

            <h3 className="text-xl font-bold text-green-600 dark:text-green-400 mb-2">
              {t('nafath.verified')}
            </h3>
            <p className="text-[var(--pv-color-text-muted)]">
              {t('nafath.verifiedDescription')}
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Failed/Rejected/Expired State
  if (status === 'failed' || status === 'rejected' || status === 'expired') {
    return (
      <Card variant="bordered" className={className}>
        <CardContent>
          <div className="text-center py-8">
            <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
              <i className={`text-4xl text-red-500 ${status === 'expired' ? 'ri-time-line' : 'ri-close-circle-fill'}`}></i>
            </div>

            <h3 className="text-xl font-bold text-red-600 dark:text-red-400 mb-2">
              {t(status === 'expired' ? 'nafath.expired' : 'nafath.failed')}
            </h3>
            <p className="text-[var(--pv-color-text-muted)] mb-6">
              {t(status === 'expired' ? 'nafath.expiredDescription' : 'nafath.failedDescription')}
            </p>

            <button
              onClick={onRetry}
              className="px-6 py-3 bg-[var(--pv-color-primary)] text-white rounded-xl font-medium hover:shadow-lg transition-shadow"
            >
              <i className="ri-refresh-line mr-2"></i>
              {t('nafath.retry')}
            </button>
          </div>
        </CardContent>
      </Card>
    );
  }

  return null;
}
